"use client";

import { useEffect, useState, useCallback, useRef } from "react";
import type { ComponentType } from "react";
import {
    FiPlus,
    FiMinus,
    FiMoon,
    FiSun,
    FiEye,
    FiLink2,
    FiPause,
    FiBookOpen,
    FiVolume2,
    FiVolumeX,
    FiX,
    FiRotateCcw,
    FiType,
    FiMousePointer,
} from "react-icons/fi";
import { MdAccessible, MdKeyboard } from "react-icons/md";
import { FaWhatsapp } from "react-icons/fa";
import { ChatbaseButton, ChatbaseWindow } from "./Chatbase";

const WHATSAPP_URL = "https://wa.me/94766360600";

type Prefs = {
    fontScale: number;
    lightMode: boolean;
    highContrast: boolean;
    dyslexiaFont: boolean;
    highlightLinks: boolean;
    pauseAnimations: boolean;
    biggerCursor: boolean;
    readingGuide: boolean;
    keyboardNav: boolean;
    soundFeedback: boolean;
};

const DEFAULTS: Prefs = {
    fontScale: 1,
    lightMode: false,
    highContrast: false,
    dyslexiaFont: false,
    highlightLinks: false,
    pauseAnimations: false,
    biggerCursor: false,
    readingGuide: false,
    keyboardNav: false,
    soundFeedback: false,
};

const STORAGE_KEY = "aurex_a11y_prefs_v1";
const MIN_SCALE = 0.85;
const MAX_SCALE = 1.4;
const STEP = 0.05;

type ToggleDef = {
    key: keyof Prefs;
    label: string;
    tip: string;
    Icon: ComponentType<{ className?: string }>;
    hz: number;
};

export default function AccessibilityWidget() {
    const [open, setOpen] = useState(false);
    const [mounted, setMounted] = useState(false);
    const [prefs, setPrefs] = useState<Prefs>(DEFAULTS);
    const [guideY, setGuideY] = useState<number | null>(null);
    const [resetPulse, setResetPulse] = useState(false);
    const [isTouch, setIsTouch] = useState(false);
    const [nearFab, setNearFab] = useState(false);
    const [chatOpen, setChatOpen] = useState(false);
    const audioCtxRef = useRef<AudioContext | null>(null);
    const fabRef = useRef<HTMLButtonElement | null>(null);
    const waFabRef = useRef<HTMLAnchorElement | null>(null);

    useEffect(() => {
        setMounted(true);
        try {
            const raw = localStorage.getItem(STORAGE_KEY);
            if (raw) {
                const parsed = JSON.parse(raw);
                setPrefs({ ...DEFAULTS, ...parsed });
            }
        } catch {
        }
    }, []);

    useEffect(() => {
        if (!mounted) return;
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
        } catch {
        }
        const html = document.documentElement;
        html.style.setProperty("--a11y-font-scale", String(prefs.fontScale));
        html.classList.toggle("a11y-light-mode", prefs.lightMode);
        html.classList.toggle("a11y-high-contrast", prefs.highContrast);
        html.classList.toggle("a11y-dyslexia", prefs.dyslexiaFont);
        html.classList.toggle("a11y-highlight-links", prefs.highlightLinks);
        html.classList.toggle("a11y-pause-animations", prefs.pauseAnimations);
        html.classList.toggle("a11y-bigger-cursor", prefs.biggerCursor);
        html.classList.toggle("a11y-keyboard-nav", prefs.keyboardNav);
    }, [prefs, mounted]);

    useEffect(() => {
        if (!prefs.readingGuide) {
            setGuideY(null);
            return;
        }
        const onMove = (e: MouseEvent) => setGuideY(e.clientY);
        window.addEventListener("mousemove", onMove, { passive: true });
        return () => window.removeEventListener("mousemove", onMove);
    }, [prefs.readingGuide]);

    useEffect(() => {
        if (!open) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") setOpen(false);
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open]);

    useEffect(() => {
        if (typeof window === "undefined") return;
        setIsTouch(window.matchMedia("(hover: none), (pointer: coarse)").matches);
    }, []);



    const playSound = useCallback(
        (freq = 540) => {
            if (!prefs.soundFeedback) return;
            try {
                const W = window as Window & { webkitAudioContext?: typeof AudioContext };
                const AC = window.AudioContext || W.webkitAudioContext;
                if (!AC) return;
                if (!audioCtxRef.current) audioCtxRef.current = new AC();
                const ctx = audioCtxRef.current;
                const o = ctx.createOscillator();
                const g = ctx.createGain();
                o.frequency.value = freq;
                o.type = "sine";
                o.connect(g);
                g.connect(ctx.destination);
                g.gain.setValueAtTime(0.0001, ctx.currentTime);
                g.gain.exponentialRampToValueAtTime(0.06, ctx.currentTime + 0.01);
                g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.18);
                o.start();
                o.stop(ctx.currentTime + 0.2);
            } catch {
            }
        },
        [prefs.soundFeedback]
    );

    const togglePref = (key: keyof Prefs, hz = 540) => {
        setPrefs((p) => ({ ...p, [key]: !p[key] }));
        playSound(hz);
    };

    const adjustFont = (dir: 1 | -1 | 0) => {
        setPrefs((p) => {
            if (dir === 0) return { ...p, fontScale: 1 };
            const next = Math.min(MAX_SCALE, Math.max(MIN_SCALE, p.fontScale + STEP * dir));
            return { ...p, fontScale: Number(next.toFixed(2)) };
        });
        playSound(dir === 0 ? 480 : dir > 0 ? 620 : 460);
    };

    const resetAll = () => {
        setPrefs(DEFAULTS);
        setResetPulse(true);
        setTimeout(() => setResetPulse(false), 900);
        playSound(720);
    };

    if (!mounted) return null;

    const togglesActive =
        Number(prefs.lightMode) +
        Number(prefs.highContrast) +
        Number(prefs.dyslexiaFont) +
        Number(prefs.highlightLinks) +
        Number(prefs.pauseAnimations) +
        Number(prefs.biggerCursor) +
        Number(prefs.readingGuide) +
        Number(prefs.keyboardNav) +
        (prefs.fontScale !== 1 ? 1 : 0);

    const fabRevealed = open || nearFab || isTouch || togglesActive > 0;

    const toggles: ToggleDef[] = [
        {
            key: "lightMode",
            label: "Light Mode",
            tip: "Invert site colors for a bright theme",
            Icon: prefs.lightMode ? FiSun : FiMoon,
            hz: 520,
        },
        {
            key: "highContrast",
            label: "High Contrast",
            tip: "Boost text and background contrast",
            Icon: FiEye,
            hz: 580,
        },
        {
            key: "dyslexiaFont",
            label: "Dyslexia-Friendly Font",
            tip: "Switch to a more readable typeface",
            Icon: FiType,
            hz: 540,
        },
        {
            key: "highlightLinks",
            label: "Highlight Links",
            tip: "Make every link clearly stand out",
            Icon: FiLink2,
            hz: 560,
        },
        {
            key: "pauseAnimations",
            label: "Pause Animations",
            tip: "Stop all motion across the site",
            Icon: FiPause,
            hz: 500,
        },
        {
            key: "biggerCursor",
            label: "Bigger Cursor",
            tip: "Enlarge the mouse pointer",
            Icon: FiMousePointer,
            hz: 600,
        },
        {
            key: "readingGuide",
            label: "Reading Guide",
            tip: "Show a focus line that follows your cursor",
            Icon: FiBookOpen,
            hz: 620,
        },
        {
            key: "keyboardNav",
            label: "Keyboard Navigation",
            tip: "Show stronger focus outlines for keyboard users",
            Icon: MdKeyboard,
            hz: 640,
        },
    ];

    return (
        <>
            {prefs.readingGuide && guideY !== null && (
                <div
                    aria-hidden="true"
                    className="pointer-events-none fixed left-0 right-0 z-[9990]"
                    style={{ top: guideY }}
                >
                    <div className="h-px bg-gradient-to-r from-transparent via-purple-400 to-transparent" />
                    <div className="-mt-6 h-12 bg-purple-500/10 mix-blend-screen" />
                </div>
            )}

            <div
                className={`fixed bottom-5 left-5 z-[99999] flex flex-col items-center gap-2.5 transition-all duration-300 ${fabRevealed ? "scale-100 opacity-100" : "scale-90 opacity-60"
                    }`}
                style={{ transformOrigin: "bottom left" }}
            >
                <div onFocus={() => setNearFab(true)} onBlur={() => setNearFab(false)} onMouseEnter={() => setNearFab(true)}>
                    <ChatbaseButton isOpen={chatOpen} setIsOpen={setChatOpen} />
                </div>

                <a
                    ref={waFabRef}
                    href={WHATSAPP_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Chat with us on WhatsApp"
                    title="Chat on WhatsApp"
                    onFocus={() => setNearFab(true)}
                    onBlur={() => setNearFab(false)}
                    onMouseEnter={() => setNearFab(true)}
                    className="a11y-no-highlight a11y-fab group relative flex h-14 w-14 items-center justify-center rounded-full bg-[#25D366] hover:bg-[#1DA851] text-white shadow-lg transition-transform duration-200 hover:scale-110 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#25D366]/70"
                >
                    <FaWhatsapp className="relative h-7 w-7" />
                </a>

                <button
                    ref={fabRef}
                    type="button"
                    aria-label={open ? "Close accessibility menu" : "Open accessibility menu"}
                    aria-expanded={open}
                    aria-controls="aurex-a11y-panel"
                    title="Accessibility"
                    onFocus={() => setNearFab(true)}
                    onBlur={() => setNearFab(false)}
                    onMouseEnter={() => setNearFab(true)}
                    onClick={() => {
                        setOpen((o) => !o);
                        playSound(open ? 440 : 660);
                    }}
                    className="a11y-fab group relative flex h-14 w-14 items-center justify-center rounded-full bg-blue-600 hover:bg-blue-700 text-white shadow-lg transition-transform duration-200 hover:scale-110 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600/70"
                >
                    <span className="relative">
                        {open ? (
                            <FiX className="h-7 w-7" />
                        ) : (
                            <MdAccessible className="h-7 w-7" />
                        )}
                    </span>
                    {!open && togglesActive > 0 && (
                        <span className="pointer-events-none absolute -top-1 -right-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-blue-400 px-1.5 text-[10px] font-bold text-[#0a0a14] shadow-lg ring-2 ring-[#0a0a0a]">
                            {togglesActive}
                        </span>
                    )}
                </button>
            </div>

            <div
                id="aurex-a11y-panel"
                role="dialog"
                aria-modal="false"
                aria-label="Accessibility settings"
                style={{ bottom: "9.5rem" }}
                className={`a11y-panel fixed left-5 z-[99999] w-[min(320px,calc(100vw-2.5rem))] origin-bottom-left rounded-2xl border border-white/10 bg-[#0a0a14]/90 shadow-[0_20px_50px_-15px_rgba(0,0,0,0.7),inset_0_1px_0_0_rgba(255,255,255,0.05)] backdrop-blur-2xl transition-all duration-300 ease-out ${open
                        ? "pointer-events-auto translate-y-0 scale-100 opacity-100"
                        : "pointer-events-none translate-y-3 scale-95 opacity-0"
                    }`}
            >
                <div
                    aria-hidden="true"
                    className="pointer-events-none absolute inset-0 rounded-2xl ring-1 ring-inset ring-purple-400/10"
                />

                <div className="relative overflow-hidden rounded-2xl">
                    <div className="flex items-center justify-between gap-2 border-b border-white/5 px-3 py-2.5">
                        <div className="flex items-center gap-2">
                            <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-purple-500/20 text-purple-300">
                                <MdAccessible className="h-4 w-4" />
                            </span>
                            <div className="flex flex-col leading-tight">
                                <span className="text-sm font-semibold text-white">Accessibility</span>
                                <span className="text-[10px] uppercase tracking-wider text-purple-400/80">
                                    {togglesActive > 0
                                        ? `${togglesActive} active`
                                        : "Personalize your view"}
                                </span>
                            </div>
                        </div>
                        <div className="flex items-center gap-1">
                            <button
                                type="button"
                                aria-label={
                                    prefs.soundFeedback
                                        ? "Disable sound feedback"
                                        : "Enable sound feedback"
                                }
                                title={
                                    prefs.soundFeedback
                                        ? "Disable sound feedback"
                                        : "Enable sound feedback"
                                }
                                onClick={() => togglePref("soundFeedback", 700)}
                                className={`flex h-8 w-8 items-center justify-center rounded-lg border border-white/5 transition-colors hover:bg-white/5 hover:text-white ${prefs.soundFeedback
                                        ? "text-purple-300"
                                        : "text-muted-foreground"
                                    }`}
                            >
                                {prefs.soundFeedback ? (
                                    <FiVolume2 className="h-4 w-4" />
                                ) : (
                                    <FiVolumeX className="h-4 w-4" />
                                )}
                            </button>
                            <button
                                type="button"
                                aria-label="Close accessibility menu"
                                title="Close"
                                onClick={() => {
                                    setOpen(false);
                                    playSound(440);
                                }}
                                className="flex h-8 w-8 items-center justify-center rounded-lg border border-white/5 text-muted-foreground transition-colors hover:bg-white/5 hover:text-white"
                            >
                                <FiX className="h-4 w-4" />
                            </button>
                        </div>
                    </div>

                    <div className="border-b border-white/5 px-3 py-2.5">
                        <div className="mb-2 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <FiType className="h-4 w-4 text-purple-300" />
                                <span className="text-sm font-medium text-white">Text Size</span>
                            </div>
                            <span className="font-mono text-xs text-purple-300/90">
                                {Math.round(prefs.fontScale * 100)}%
                            </span>
                        </div>
                        <div className="grid grid-cols-3 gap-2">
                            <button
                                type="button"
                                aria-label="Decrease text size"
                                title="Decrease text size"
                                onClick={() => adjustFont(-1)}
                                disabled={prefs.fontScale <= MIN_SCALE}
                                className="flex items-center justify-center gap-1 rounded-lg border border-white/5 bg-white/[0.03] py-2 text-sm text-white/90 transition-all hover:scale-[1.03] hover:border-purple-400/40 hover:bg-purple-500/15 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:scale-100"
                            >
                                <FiMinus className="h-3.5 w-3.5" /> A
                            </button>
                            <button
                                type="button"
                                aria-label="Reset text size"
                                title="Reset text size"
                                onClick={() => adjustFont(0)}
                                className="flex items-center justify-center gap-1 rounded-lg border border-white/5 bg-white/[0.03] py-2 text-sm text-white/90 transition-all hover:scale-[1.03] hover:border-purple-400/40 hover:bg-purple-500/15"
                            >
                                <FiRotateCcw className="h-3.5 w-3.5" />
                            </button>
                            <button
                                type="button"
                                aria-label="Increase text size"
                                title="Increase text size"
                                onClick={() => adjustFont(1)}
                                disabled={prefs.fontScale >= MAX_SCALE}
                                className="flex items-center justify-center gap-1 rounded-lg border border-white/5 bg-white/[0.03] py-2 text-sm text-white/90 transition-all hover:scale-[1.03] hover:border-purple-400/40 hover:bg-purple-500/15 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:scale-100"
                            >
                                <FiPlus className="h-3.5 w-3.5" /> A
                            </button>
                        </div>
                    </div>

                    <ul
                        data-lenis-prevent
                        className="a11y-scroll max-h-[35vh] overflow-y-auto overscroll-contain px-2 py-2"
                    >
                        {toggles.map(({ key, label, tip, Icon, hz }) => {
                            const active = Boolean(prefs[key]);
                            return (
                                <li key={key}>
                                    <button
                                        type="button"
                                        role="switch"
                                        aria-checked={active}
                                        aria-label={`${label}: ${active ? "on" : "off"}`}
                                        title={tip}
                                        onClick={() => togglePref(key, hz)}
                                        className={`group flex w-full items-center justify-between gap-3 rounded-xl border border-transparent px-2.5 py-2 transition-all duration-200 hover:scale-[1.01] hover:border-white/5 hover:bg-white/[0.04] focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-400/60 ${active ? "bg-purple-500/[0.08]" : ""
                                            }`}
                                    >
                                        <span className="flex items-center gap-3 text-sm text-white/90">
                                            <span
                                                className={`flex h-7 w-7 items-center justify-center rounded-lg border transition-colors ${active
                                                        ? "border-purple-400/50 bg-purple-500/20 text-purple-200"
                                                        : "border-white/5 bg-white/[0.03] text-muted-foreground group-hover:text-white/90"
                                                    }`}
                                            >
                                                <Icon className="h-4 w-4" />
                                            </span>
                                            <span className="font-medium">{label}</span>
                                        </span>
                                        <span
                                            aria-hidden="true"
                                            className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full border transition-colors duration-300 ${active
                                                    ? "border-purple-400/60 bg-purple-500/70"
                                                    : "border-white/10 bg-white/5"
                                                }`}
                                        >
                                            <span
                                                className={`absolute h-4 w-4 rounded-full shadow-md transition-all duration-300 ${active
                                                        ? "left-[24px] bg-white shadow-purple-500/40"
                                                        : "left-[3px] bg-white/70"
                                                    }`}
                                            />
                                        </span>
                                    </button>
                                </li>
                            );
                        })}
                    </ul>

                    <div className="border-t border-white/5 px-3 py-2.5">
                        <button
                            type="button"
                            aria-label="Reset all accessibility settings"
                            title="Reset all accessibility settings"
                            onClick={resetAll}
                            className={`relative flex w-full items-center justify-center gap-2 rounded-xl border border-purple-400/30 bg-purple-500/10 px-3 py-2 text-sm font-semibold text-purple-200 transition-all duration-300 hover:scale-[1.02] hover:border-purple-400/60 hover:bg-purple-500/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-400/80 ${resetPulse ? "a11y-reset-pulse" : ""
                                }`}
                        >
                            <FiRotateCcw
                                className={`h-4 w-4 transition-transform duration-500 ${resetPulse ? "rotate-[360deg]" : ""
                                    }`}
                            />
                            <span>Reset All Settings</span>
                        </button>
                    </div>
                </div>
            </div>
            
            <ChatbaseWindow isOpen={chatOpen} setIsOpen={setChatOpen} />
        </>
    );
}
